<template>
  <div class="tw-relative">
    <div v-if="firstTimeLoading" class="tw-flex tw-items-center tw-justify-center">
      <WdsSpinner indeterminate :size="40" />
    </div>
    <div v-else>
      <div ref="tabsRef" class="tw-mt-6">
        <WdsTabs
          :active-tab-key="jobStore.activeTabKey"
          secondary
          @update:active-tab-key="changeTab"
        >
          <WdsTab :title="$t('Overview')" :tab-key="JOB_TAB.OVERVIEW" data-test="overview-tab">
            <template #title>
              <TabTitle :title="$t('Overview')" :tab-key="JOB_TAB.OVERVIEW" />
            </template>

            <OverviewTab />
          </WdsTab>
          <WdsTab
            :title="$t('Details')"
            :tab-key="JOB_TAB.DETAILS"
            :disabled="!jobStore.hasPersistedJob"
            data-test="details-tab"
          >
            <template #title>
              <TabTitle :title="$t('Details')" :tab-key="JOB_TAB.DETAILS" />
            </template>

            <DetailsTab />
          </WdsTab>
          <WdsTab
            :title="$t('Budget')"
            :tab-key="JOB_TAB.BUDGET"
            :disabled="!jobStore.hasPersistedJob"
            data-test="budget-tab"
          >
            <template #title>
              <TabTitle :title="$t('Budget')" :tab-key="JOB_TAB.BUDGET" />
            </template>

            <BudgetTab />
          </WdsTab>
          <WdsTab
            :title="$t('Settings')"
            :tab-key="JOB_TAB.SETTINGS"
            :disabled="!jobStore.hasPersistedJob"
            data-test="settings-tab"
            justify-right
          >
            <SettingsTab />
          </WdsTab>
        </WdsTabs>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { WdsSpinner, WdsTab, WdsTabs } from '@worksome/wds-components'
import { computed, ref } from 'vue'

import useNavigateWarning from '@/composables/useNavigateWarning'
import { $t } from '@/composables/translation'
import { useFieldableFormStore } from '@/components/Fieldable/store/useFieldableFormStore'
import { JOB_TAB } from '@/components/Job/constants'
import OverviewTab from '@/components/Job/Edit/Tabs/OverviewTab.vue'
import DetailsTab from '@/components/Job/Edit/Tabs/DetailsTab.vue'
import BudgetTab from '@/components/Job/Edit/Tabs/BudgetTab.vue'
import SettingsTab from '@/components/Job/Edit/Tabs/SettingsTab.vue'
import TabTitle from '@/components/Job/Edit/Tabs/TabTitle.vue'
import { useJobFormStore } from '@/components/Job/Edit/store/jobFormStore'

defineProps({
  /**
   *  Indicates whether any loading is occurring and displays a small spinner on the ActionBar
   */
  loading: {
    type: Boolean,
    required: false,
    default: false,
  },

  /**
   * Indicates whether it is the first time loading a job and displays a spinner over the
   * component content until the job is loaded.
   */
  firstTimeLoading: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const emit = defineEmits(['update:active-tab-key'])

const tabsRef = ref<HTMLElement>()
defineExpose({ tabsRef })

const jobStore = useJobFormStore()
const fieldableStore = useFieldableFormStore()

const formIsDirty = computed(() => jobStore.formIsDirty || fieldableStore.formIsDirty)

useNavigateWarning(formIsDirty)

const changeTab = (tab: JOB_TAB) => {
  if (jobStore.activeTabKey !== tab) {
    emit('update:active-tab-key', tab)
  }
}
</script>

<style scoped lang="scss">
:deep(.wds-tab-content) {
  @apply tw-overflow-y-visible;
}
</style>
